import {Button} from '@wandb/weave/components/Button';
import {Checkbox} from '@wandb/weave/components/Checkbox';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {isNil as _isNil} from 'lodash';
import React, {type FC, useState} from 'react';
import {Dropdown} from 'semantic-ui-react';

import type * as FilterTypes from '../../util/filterTypes';
import {
  type ConnectorOp,
  EMPTY_PLACEHOLDER_FILTER,
  type FilterV2Group,
  type FilterV2Root,
  type FlatIndividualItem,
  getConnector,
  insertFilterV2At,
  insertFilterV2GroupAt,
  isFilterV2Group,
  removeFilterV2At,
  removeFilterV2GroupAt,
  setFilterV2GroupAtPath,
  toggleGroupDisabled,
  updateFilterV2GroupAt,
  updateFilterV2RootAt,
} from '../../util/filterV2';
import type * as RunTypes from '../../util/runTypes';
import {EditableFilterContextProvider} from './FilterContext';
import {FilterEditable} from './FilterEditable';
import {type FilterKeySelectorCreatorProps} from './FilterKeySelector';
import {type FilterValueSelectorCreatorProps} from './FilterValueSelector';
import {handleFilterOpChange} from './logic';

const AndOrDropdown: FC<{
  value: ConnectorOp;
  onChange: (op: ConnectorOp) => void;
}> = React.memo(({value, onChange}) => (
  <Dropdown
    className="filter-dropdown filter-list__operation"
    data-test="filter-and-or-dropdown"
    options={[
      {text: 'AND', value: 'AND'},
      {text: 'OR', value: 'OR'},
    ]}
    selectOnNavigation={false}
    value={value}
    onChange={(_e, {value: v}) => onChange((v as ConnectorOp) ?? 'AND')}
    aria-label="Match type (AND or OR)"
  />
));
AndOrDropdown.displayName = 'AndOrDropdown';

const WHERE_PLACEHOLDER = (
  <span className="filter-dropdown filter-list__operation">WHERE</span>
);

function renderLeadingContent(
  index: number,
  item: FlatIndividualItem | FilterV2Group,
  onConnectorChange: (op: ConnectorOp) => void
): React.ReactNode {
  if (index === 0) {
    return WHERE_PLACEHOLDER;
  }
  return (
    <AndOrDropdown
      value={getConnector(item) ?? 'AND'}
      onChange={onConnectorChange}
    />
  );
}

interface FilterItemRowProps {
  item: FlatIndividualItem;
  index: number;
  filterIndex: number;
  justAdded: boolean;
  leadingContent: React.ReactNode;
  setFilterAtIndex: (
    i: number,
    f: FilterTypes.IndividualFilter<FilterTypes.FilterKey>
  ) => void;
  deleteFilterAtIndex: (i: number) => void;
  filterKeySelector: (props: FilterKeySelectorCreatorProps) => React.ReactNode;
  filterValueSelector: (
    props: FilterValueSelectorCreatorProps<FilterTypes.FilterKey>
  ) => React.ReactNode;
}

const FilterItemRow: FC<FilterItemRowProps> = React.memo(
  ({
    item,
    index,
    filterIndex,
    justAdded,
    leadingContent,
    setFilterAtIndex,
    deleteFilterAtIndex,
    filterKeySelector,
    filterValueSelector,
  }) => (
    <FilterEditable
      filterIndex={filterIndex}
      justAdded={justAdded}
      filter={item}
      leadingContent={leadingContent}
      setFilterDisabled={(disabled: boolean) => {
        setFilterAtIndex(index, {...item, disabled});
      }}
      setFilter={filt => setFilterAtIndex(index, filt)}
      setFilterOp={(newFilterOp: FilterTypes.IndividualOp) => {
        setFilterAtIndex(index, handleFilterOpChange(item, newFilterOp));
      }}
      setFilterValue={(filterValue: RunTypes.Value) => {
        setFilterAtIndex(index, {
          ...item,
          value: filterValue,
          disabled: _isNil(filterValue),
        } as FilterTypes.ValueFilter<FilterTypes.FilterKey> &
          FilterTypes.ToggleableFilter);
      }}
      setFilterMeta={(meta: FilterTypes.FilterMeta) => {
        setFilterAtIndex(index, {
          ...item,
          meta,
        } as FilterTypes.ValueFilter<FilterTypes.FilterKey>);
      }}
      setFilterMultiValue={(filterValue: RunTypes.Value[]) => {
        setFilterAtIndex(index, {
          ...item,
          value: filterValue,
          disabled: _isNil(filterValue),
        } as FilterTypes.MultiValueFilter<FilterTypes.FilterKey> &
          FilterTypes.ToggleableFilter);
      }}
      deleteFilter={() => deleteFilterAtIndex(index)}
      filterKeySelector={filterKeySelector}
      filterValueSelector={filterValueSelector}
    />
  )
);
FilterItemRow.displayName = 'FilterItemRow';

/**
 * Abstracts the difference between root-level and group-level filter
 * mutations so that a single inner component can handle both depths.
 */
interface FilterListOps {
  update(index: number, item: FlatIndividualItem | FilterV2Group): void;
  remove(index: number): void;
  insert(index: number, item: FlatIndividualItem | FilterV2Group): void;
}

interface FilterListV2InnerProps {
  filters: Array<FlatIndividualItem | FilterV2Group>;
  ops: FilterListOps;
  depth: 0 | 1;
  canAdd?: boolean;
  onGroupCreated?: () => void;
  onORToggled?: () => void;
  onGroupChange?: (index: number, group: FilterV2Group) => void;
  onToggleGroup?: (index: number) => void;
  onDeleteGroup?: () => void;
  filterKeySelector(props: FilterKeySelectorCreatorProps): React.ReactNode;
  filterValueSelector(
    props: FilterValueSelectorCreatorProps<FilterTypes.FilterKey>
  ): React.ReactNode;
}

/**
 * Shared implementation for both the root filter list and nested groups.
 * At depth 0 it renders group containers and the "New group" button;
 * at depth 1 it renders only flat filters (max nesting = 1).
 */
const FilterListV2Inner: FC<FilterListV2InnerProps> = React.memo(
  ({
    filters,
    ops,
    depth,
    canAdd = true,
    onGroupCreated,
    onORToggled,
    onGroupChange,
    onToggleGroup,
    onDeleteGroup,
    filterKeySelector,
    filterValueSelector,
  }) => {
    const [addedFilterIndex, setAddedFilterIndex] = useState(-1);

    const setFilterAtIndex = (
      index: number,
      filter: FilterTypes.IndividualFilter<FilterTypes.FilterKey>
    ) => {
      if (isFilterV2Group(filters[index])) {
        return;
      }
      ops.update(index, filter as FlatIndividualItem);
    };

    const deleteFilterAtIndex = (index: number) => {
      if (isFilterV2Group(filters[index])) {
        return;
      }
      ops.remove(index);
    };

    const setConnectorAtIndex = (index: number, op: ConnectorOp) => {
      const existing = filters[index];
      if (existing == null) {
        return;
      }
      if (op === 'OR') {
        onORToggled?.();
      }
      ops.update(index, {...existing, connector: op});
    };

    const isRoot = depth === 0;

    return (
      <div
        className={
          isRoot
            ? 'flex flex-col gap-16 [&_.flex-grow]:!max-w-[600px] [&_.filter-list__value]:!max-w-[600px]'
            : 'flex flex-col gap-12'
        }>
        <EditableFilterContextProvider
          actions={{
            deleteFilter: deleteFilterAtIndex,
            setFilter: setFilterAtIndex,
          }}
          filters={
            filters as Array<
              FilterTypes.IndividualFilter<FilterTypes.FilterKey>
            >
          }>
          {filters.map((item, i) => {
            if (isFilterV2Group(item)) {
              if (!isRoot) {
                return null;
              }
              return (
                // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
                <div key={`group-${i}`} className="flex items-start gap-8">
                  <div className="flex shrink-0 items-center gap-8 pt-12">
                    <div className="flex h-24 w-24 items-center justify-center">
                      <Checkbox
                        size="small"
                        data-test="filter-group-toggle"
                        checked={!item.disabled}
                        onCheckedChange={() => onToggleGroup?.(i)}
                      />
                    </div>
                    <div className="flex w-[100px] items-center justify-start">
                      {renderLeadingContent(i, item, op =>
                        setConnectorAtIndex(i, op)
                      )}
                    </div>
                  </div>
                  <div className="relative min-w-0 flex-1 rounded border border-moon-250 p-12">
                    <FilterListV2Inner
                      filters={item.filters}
                      ops={{
                        update: (idx, updated) =>
                          onGroupChange?.(
                            i,
                            updateFilterV2GroupAt(
                              item,
                              idx,
                              updated as FlatIndividualItem
                            )
                          ),
                        remove: idx =>
                          onGroupChange?.(i, removeFilterV2GroupAt(item, idx)),
                        insert: (idx, newItem) =>
                          onGroupChange?.(
                            i,
                            insertFilterV2GroupAt(
                              item,
                              idx,
                              newItem as FlatIndividualItem
                            )
                          ),
                      }}
                      depth={1}
                      onORToggled={onORToggled}
                      onDeleteGroup={() => ops.remove(i)}
                      filterKeySelector={filterKeySelector}
                      filterValueSelector={filterValueSelector}
                    />
                  </div>
                </div>
              );
            }

            const leadingContent = renderLeadingContent(i, item, op =>
              setConnectorAtIndex(i, op)
            );

            const row = (
              <FilterItemRow
                key={isRoot ? `filter-${i}` : `group-filter-${i}`}
                item={item}
                index={i}
                filterIndex={i}
                justAdded={addedFilterIndex === i}
                leadingContent={leadingContent}
                setFilterAtIndex={setFilterAtIndex}
                deleteFilterAtIndex={deleteFilterAtIndex}
                filterKeySelector={filterKeySelector}
                filterValueSelector={filterValueSelector}
              />
            );

            return isRoot ? (
              // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
              <div key={`filter-${i}`} className="pr-12">
                {row}
              </div>
            ) : (
              row
            );
          })}
          {canAdd && isRoot && (
            <div className="flex gap-8">
              <Button
                icon="add-new"
                data-test="filter-add"
                onClick={() => {
                  setAddedFilterIndex(filters.length);
                  ops.insert(filters.length, EMPTY_PLACEHOLDER_FILTER);
                }}>
                New filter
              </Button>
              <Button
                icon="add-new"
                variant="secondary"
                data-test="filter-add-group"
                onClick={() => {
                  onGroupCreated?.();
                  ops.insert(filters.length, {
                    filters: [EMPTY_PLACEHOLDER_FILTER],
                    disabled: true,
                  });
                }}>
                New group
              </Button>
            </div>
          )}
          {!isRoot && (
            <div className="flex items-center justify-between">
              {canAdd !== false && (
                <Button
                  icon="add-new"
                  size="small"
                  variant="ghost"
                  data-test="filter-group-add"
                  onClick={() => {
                    setAddedFilterIndex(filters.length);
                    ops.insert(filters.length, {
                      ...EMPTY_PLACEHOLDER_FILTER,
                    });
                  }}>
                  New filter
                </Button>
              )}
              {onDeleteGroup && (
                <Button
                  size="small"
                  variant="ghost"
                  data-test="filter-group-delete"
                  onClick={onDeleteGroup}>
                  Delete group
                </Button>
              )}
            </div>
          )}
        </EditableFilterContextProvider>
      </div>
    );
  }
);
FilterListV2Inner.displayName = 'FilterListV2Inner';

// ---------------------------------------------------------------------------
// Public API — thin wrapper that constructs root-level ops
// ---------------------------------------------------------------------------

interface FilterListV2Props {
  root: FilterV2Root;
  onChange: (newRoot: FilterV2Root) => void;
  canAdd?: boolean;
  onGroupCreated?: () => void;
  onORToggled?: () => void;
  filterKeySelector(props: FilterKeySelectorCreatorProps): React.ReactNode;
  filterValueSelector(
    props: FilterValueSelectorCreatorProps<FilterTypes.FilterKey>
  ): React.ReactNode;
}

/**
 * EditableFilterContextProvider note:
 *
 * It currently exists only to support the Infinite Loading Tags filter,
 * written with an eye toward eventually being the single source of truth
 * for all editable filters. The context avoids prop-drilling so that
 * downstream consumers can access filter actions without threading
 * callbacks through every intermediate layer.
 *
 * WARNING: the types are (maybe) a lie! We operate on a narrow slice of
 * the underlying filter type complexity and cast liberally. Revisit if
 * more use cases come to be included in EditableFilterContextProvider.
 */
const FilterListV2: FC<FilterListV2Props> = React.memo(
  ({
    root,
    onChange,
    canAdd = true,
    onGroupCreated,
    onORToggled,
    filterKeySelector,
    filterValueSelector,
  }) => {
    const handleGroupChange = (
      groupIndex: number,
      updatedGroup: FilterV2Group
    ) => {
      if (updatedGroup.filters.length === 0) {
        onChange(removeFilterV2At(root, groupIndex));
        return;
      }
      const oldGroup = root.filters[groupIndex];
      const shouldAutoEnable =
        isFilterV2Group(oldGroup) &&
        oldGroup.disabled &&
        updatedGroup.filters.some((f, idx) => {
          if (isFilterV2Group(f) || f.disabled) {
            return false;
          }
          const oldChild = oldGroup.filters[idx];
          return (
            oldChild != null && !isFilterV2Group(oldChild) && oldChild.disabled
          );
        });
      const group = shouldAutoEnable
        ? {...updatedGroup, disabled: false}
        : updatedGroup;
      onChange(setFilterV2GroupAtPath(root, [groupIndex], group));
    };

    const handleToggleGroup = (groupIndex: number) => {
      onChange(toggleGroupDisabled(root, groupIndex));
    };

    const ops: FilterListOps = {
      update: (index, item) =>
        onChange(updateFilterV2RootAt(root, index, item as FlatIndividualItem)),
      remove: index => onChange(removeFilterV2At(root, index)),
      insert: (index, item) =>
        onChange(insertFilterV2At(root, index, item as FlatIndividualItem)),
    };

    return (
      <Tailwind>
        <FilterListV2Inner
          filters={root.filters}
          ops={ops}
          depth={0}
          canAdd={canAdd}
          onGroupCreated={onGroupCreated}
          onORToggled={onORToggled}
          onGroupChange={handleGroupChange}
          onToggleGroup={handleToggleGroup}
          filterKeySelector={filterKeySelector}
          filterValueSelector={filterValueSelector}
        />
      </Tailwind>
    );
  }
);
FilterListV2.displayName = 'FilterListV2';

export default FilterListV2;
